import { useEffect, useState } from "react";
import { CalendarPlus, Check, X, ChevronDown, ChevronUp } from "lucide-react";
import api from "../lib/api";

export default function AdminEventSubmissions() {
    const [tab, setTab] = useState("pending");
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState({});
    const [acting, setActing] = useState("");

    const reload = () => {
        api.get(`/admin/event-submissions?status=${tab}&limit=200`)
            .then((r) => setItems(r.data || []))
            .catch(() => setItems([]));
    };

    useEffect(() => { reload(); }, [tab]);

    const approve = async (id) => {
        if (!confirm("Publier cet événement maintenant ?")) return;
        setActing(id);
        try {
            await api.post(`/admin/event-submissions/${id}/approve`, {});
            reload();
        } finally {
            setActing("");
        }
    };

    const reject = async (id) => {
        const reason = prompt("Raison du refus (envoyée au soumetteur) ? Min. 5 caractères.");
        if (!reason || reason.trim().length < 5) return;
        setActing(id);
        try {
            await api.post(`/admin/event-submissions/${id}/reject`, { reason: reason.trim() });
            reload();
        } finally {
            setActing("");
        }
    };

    const TABS = [
        { id: "pending", label: "À modérer" },
        { id: "approved", label: "Acceptés" },
        { id: "rejected", label: "Refusés" },
    ];

    return (
        <section id="submissions" className="bg-cp-ivoire border border-cp-border p-8 lg:col-span-2" data-testid="admin-event-submissions">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <p className="cp-kicker text-cp-foret flex items-center gap-2">
                    <CalendarPlus size={14} strokeWidth={1.5} /> Soumissions d'événements
                </p>
                <div className="flex border border-cp-border">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`submission-tab-${t.id}`}
                            className={`cp-tiny px-3 py-1.5 transition-colors ${
                                tab === t.id ? "bg-cp-foret text-cp-ivoire" : "bg-cp-ivoire text-cp-gris hover:text-cp-noir"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {items.length === 0 ? (
                <p className="text-cp-gris italic py-8 text-center">Aucune soumission {tab === "pending" ? "en attente" : tab === "approved" ? "acceptée" : "refusée"}.</p>
            ) : (
                <ul className="divide-y divide-cp-border">
                    {items.map((s) => {
                        const isOpen = open[s.id];
                        return (
                            <li key={s.id} className="py-4" data-testid={`submission-${s.id}`}>
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <p className="cp-serif text-lg text-cp-noir">{s.title}</p>
                                        <p className="cp-tiny text-cp-gris mt-1">
                                            {s.start_date}{s.end_date ? ` → ${s.end_date}` : ""} · {s.venue}, {s.city}
                                        </p>
                                        <p className="cp-tiny text-cp-gris mt-1">
                                            Par <strong>{s.submitter_name}</strong> · {s.submitter_email}
                                            {s.submitter_org && ` · ${s.submitter_org}`}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <button
                                            onClick={() => setOpen({ ...open, [s.id]: !isOpen })}
                                            data-testid={`submission-toggle-${s.id}`}
                                            className="cp-tiny text-cp-noir hover:text-cp-foret flex items-center gap-1"
                                        >
                                            {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                            {isOpen ? "Fermer" : "Détails"}
                                        </button>
                                        {tab === "pending" && (
                                            <>
                                                <button
                                                    onClick={() => approve(s.id)}
                                                    disabled={acting === s.id}
                                                    data-testid={`submission-approve-${s.id}`}
                                                    className="cp-tiny text-cp-foret border border-cp-foret px-3 py-1 hover:bg-cp-foret hover:text-cp-ivoire transition-colors flex items-center gap-1"
                                                >
                                                    <Check size={11} /> Publier
                                                </button>
                                                <button
                                                    onClick={() => reject(s.id)}
                                                    disabled={acting === s.id}
                                                    data-testid={`submission-reject-${s.id}`}
                                                    className="cp-tiny text-red-700 border border-red-700/30 px-3 py-1 hover:bg-red-700 hover:text-cp-ivoire transition-colors flex items-center gap-1"
                                                >
                                                    <X size={11} /> Refuser
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                                {isOpen && (
                                    <div className="mt-4 bg-cp-craie p-5 border border-cp-border space-y-3 text-sm" data-testid={`submission-detail-${s.id}`}>
                                        <p className="text-cp-noir whitespace-pre-line leading-relaxed">{s.description}</p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 cp-tiny text-cp-gris pt-2 border-t border-cp-border">
                                            <p><strong>Catégorie:</strong> {s.category}</p>
                                            {s.start_time && <p><strong>Heure:</strong> {s.start_time}</p>}
                                            {s.address && <p><strong>Adresse:</strong> {s.address}</p>}
                                            {s.price_info && <p><strong>Tarif:</strong> {s.price_info}</p>}
                                            {s.submitter_phone && <p><strong>Tél.:</strong> {s.submitter_phone}</p>}
                                            <p><strong>Soumis le:</strong> {new Date(s.created_at).toLocaleDateString("fr-FR")}</p>
                                            {s.website && <p className="md:col-span-2"><strong>Site:</strong> <a href={s.website} target="_blank" rel="noreferrer" className="text-cp-foret underline">{s.website}</a></p>}
                                            {s.image_url && <p className="md:col-span-2"><strong>Photo:</strong> <a href={s.image_url} target="_blank" rel="noreferrer" className="text-cp-foret underline">{s.image_url}</a></p>}
                                        </div>
                                        {s.image_url && (
                                            <img src={s.image_url} alt={s.title} className="max-h-64 object-cover border border-cp-border" />
                                        )}
                                        {s.rejection_reason && (
                                            <p className="cp-tiny text-red-700 italic border-t border-cp-border pt-2">
                                                <strong>Motif du refus :</strong> {s.rejection_reason}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}
